import { getPrice2decimal, removeComma, solveDecimalJavascriptSum } from 'utils'

import { configuredAmount } from './configuredBudget'
import { openingBalanceSeed } from './openingBalance'

/** One figure on the Construction Reserve summary row: key, label, value, unit. */
export type ReserveTile = [string, string, string | number, string]

/** Everything the summary figures are struck from. The same payload the loan tab already holds. */
export interface ReserveBalanceInput {
  rows: Array<Record<string, any>>
  interestRows: Array<Record<string, any>>
  rehabBudget: number
  constructionReserve: number
  interestReserve: number
  initialLoanAmount: number
  actualInitialLoanAmount: number
  proposedLoanAmount: number
  afterRepairValue: number
  chargeInterestOnReserve: boolean
  /** The panel's resolved Dutch / Non Dutch answer, not `loan.interestType`. */
  interestType: string
  /** Principal the borrower has repaid, netted off Current UPB. */
  paidPrincipal: number
  /**
   * The loan's credit-line state. `revolving` is the only thing that says whether the rest of it
   * means anything: the service returns a fully-zeroed object for a closed-end loan rather than
   * null, so a truthiness check here would show every construction loan an empty Credit Limit /
   * Available Credit set in place of its reserve figures.
   */
  creditLine?: {
    revolving?: boolean
    commitment: number
    outstanding: number
    available: number
    advancedToDate: number
    frozen?: boolean
  } | null
}

/** The fees that leave the reserve with a draw, as one figure per row. */
const rowFee = (row: Record<string, any>) =>
  solveDecimalJavascriptSum([removeComma(row.inspectionFee), removeComma(row.wireFee), removeComma(row.titleUpdateFee)])

/**
 * What the reserve has left: open at the configured reserve (unless an explicit opening deposit
 * already supplies it) and walk every row, taking off payments, fees and holdback and adding
 * deposits back. With no rows at all the whole reserve remains.
 */
export const remainingConstructionReserve = (input: ReserveBalanceInput): number => {
  const { rows, constructionReserve } = input
  if (!rows.length) return constructionReserve === -1 ? 0 : removeComma(constructionReserve)
  let balance = constructionReserve === -1 ? 0 : openingBalanceSeed(constructionReserve, rows)
  rows.forEach((row) => {
    balance = solveDecimalJavascriptSum([
      balance,
      -1 * removeComma(row.payment),
      removeComma(row.deposit),
      -1 * rowFee(row),
      -1 * removeComma(row.additionHoldback),
    ])
  })
  return balance
}

/**
 * What the reserve was funded with, as the ledger itself states it: every deposit on the bucket.
 *
 * A loan converted from a prior servicer carries no configured reserve. The conversion wrote none,
 * and it typed every loan it carried as Bridge / Purchase, which does not expose
 * `constructionReserve` at all, so the backend reports -1 for it however much construction money
 * the loan holds. Longhorn 314428 is the case: $84,600 funded as `ESCROWREP` sits plainly on the
 * grid while the summary row above it had no reserve figure to show.
 *
 * Deposits are the right read because they are the same money `remainingConstructionReserve`
 * opens on, so the pair cannot disagree: remaining is these deposits less every payment, fee and
 * holdback taken since. Deriving the configured amount instead and seeding the walk with it would
 * count the funding deposit twice.
 */
export const ledgerFundedReserve = (rows: Array<Record<string, any>>): number =>
  solveDecimalJavascriptSum(rows.map((row) => removeComma(row.deposit)))

/**
 * Total Draw Disbursed: only rows of type `Draw`, so a Transfer or a plain Disbursement does not
 * read as construction money going out. Matches how the per-row balance is drawn down.
 */
export const totalDrawDisbursed = (rows: Array<Record<string, any>>): number =>
  rows
    .filter((row) => row.type === 'Draw')
    .reduce(
      (sum, row) =>
        solveDecimalJavascriptSum([
          sum,
          removeComma(row.payment),
          -1 * removeComma(row.deposit),
          rowFee(row),
          removeComma(row.additionHoldback),
        ]),
      0,
    )

/**
 * Interest reserve that has been advanced back to the borrower.
 *
 * A Non-Dutch loan's reserve is held out of `actualInitialLoanAmount` because it is committed but
 * handed over one interest payment at a time; each payout from the Interest Reserve Ledger is
 * that money being advanced. Clamped into [0, reserve] so the balance can only ever give back the
 * reserve that was held out of it. A loan charging interest on its reserve never gave it up, so
 * it has nothing to advance and counting the payouts would state the reserve twice.
 */
export const advancedInterestReserve = (input: ReserveBalanceInput): number => {
  const { interestRows, interestReserve, chargeInterestOnReserve } = input
  if (chargeInterestOnReserve) return 0
  // An empty `type` is the legacy Interest Reserve ledger.
  const reserveRows = interestRows.filter((row) => !row.type || row.type === 'interest')
  const reserve = interestReserve > 0 ? removeComma(interestReserve) : 0
  const seed = reserve > 0 && !reserveRows.some((row) => row.isOpeningBalance === true) ? reserve : 0
  const balance = reserveRows.length
    ? solveDecimalJavascriptSum([
        seed,
        ...reserveRows.map((row) => removeComma(row.deposit)),
        ...reserveRows.map((row) => -1 * removeComma(row.payment)),
      ])
    : seed
  return Math.min(Math.max(0, reserve - balance), Math.max(0, reserve))
}

/**
 * Current UPB as servicing states it: a Dutch loan is its proposed amount, a Non-Dutch one is the
 * initial advance plus what the draws and the interest reserve have advanced since, less principal
 * repaid. Never negative.
 */
export const currentUpbForReserve = (input: ReserveBalanceInput): number => {
  const isDutch = input.interestType === 'Dutch'
  const isNonDutchExcludingIR = input.interestType === 'Non Dutch Excluding IR'
  const disbursed = totalDrawDisbursed(input.rows)
  const base = isDutch
    ? Number(input.proposedLoanAmount)
    : isNonDutchExcludingIR
    ? solveDecimalJavascriptSum([removeComma(input.actualInitialLoanAmount), removeComma(disbursed)])
    : solveDecimalJavascriptSum([
        removeComma(input.actualInitialLoanAmount),
        removeComma(disbursed),
        advancedInterestReserve(input),
      ])
  return Math.max(0, Number((base - removeComma(input.paidPrincipal)).toFixed(2)))
}

/**
 * The Construction Reserve summary figures, in the order the loan tab shows them.
 *
 * Shared with the pipeline's Draw modal so the balances a servicer reads before entering a draw
 * are the ones the loan file would have shown them. A revolving line is measured against its
 * credit limit rather than a construction budget: "Remaining Construction Reserve" is the wrong
 * question there and goes negative the first time the line is re-drawn, so it gets its own set.
 */
export const buildReserveTiles = (input: ReserveBalanceInput): ReserveTile[] => {
  const { rehabBudget, constructionReserve, creditLine, interestType } = input

  if (creditLine?.revolving) {
    return [
      ['creditLimit', 'Credit Limit', creditLine.commitment, '$'],
      ['outstanding', 'Principal Outstanding', creditLine.outstanding, '$'],
      [
        'availableCredit',
        creditLine.frozen ? 'Available Credit (draw period closed)' : 'Available Credit',
        creditLine.available,
        '$',
      ],
      ['totalAdvanced', 'Total Advanced to Date', creditLine.advancedToDate, '$'],
    ]
  }

  const disbursed = totalDrawDisbursed(input.rows)
  const remaining = remainingConstructionReserve(input)
  const isDutch = interestType === 'Dutch'
  // Read through `configuredAmount` so a figure the product type hides (-1) and one left blank (0)
  // are the same absence here. Asking only about -1 printed a blank Fix and Flip loan a $0.00
  // Rehab Budget, a $0.00 Construction Reserve and a Percent of `NaN%`.
  const budget = configuredAmount(rehabBudget)
  const reserve = configuredAmount(constructionReserve)

  // With no configured reserve to read, the ledger's own funding answers for it: a converted loan
  // never had one, and a loan whose product does expose the field can still have it left blank.
  const funded = reserve > 0 ? 0 : ledgerFundedReserve(input.rows)

  const tiles: ReserveTile[] =
    budget > 0
      ? [
          ['rehabBudget', 'Rehab Budget', budget, '$'],
          ['constructionReserve', 'Construction Reserve', reserve, '$'],
          ['percent', 'Percent', ((reserve * 100) / budget).toFixed(2), '%'],
          ['totalDisbursed', 'Total Disbursed', disbursed, '$'],
          ['constructionBalance', 'Remaining Construction Reserve', remaining, '$'],
          ['initialLoanAmount', 'Current UPB', currentUpbForReserve(input), '$'],
        ]
      : // A loan can carry a reserve with no rehab budget; the pair would otherwise be lost.
      reserve > 0
      ? [
          ['constructionReserve', 'Construction Reserve', reserve, '$'],
          ['constructionBalance', 'Remaining Construction Reserve', remaining, '$'],
        ]
      : // Neither figure is configured, but the ledger is funded: a loan carried over from a prior
      // servicer, or one nobody filled the structure in for. State the reserve it is actually
      // holding rather than nothing at all.
      funded > 0
      ? [
          ['constructionReserve', 'Construction Reserve', funded, '$'],
          ['constructionBalance', 'Remaining Construction Reserve', remaining, '$'],
        ]
      : []

  tiles.push([
    'interestReserve',
    'Initial Advance + Total Draw Disbursed',
    ((isDutch ? input.initialLoanAmount : input.actualInitialLoanAmount) + disbursed).toFixed(2),
    '$',
  ])
  // Both of these measure the rehab budget, so with no budget configured they stated the absence
  // as a percentage: a converted loan read "-0.00%" of its loan amount and "-Infinity%" of its
  // after-repair value from the -1 sentinel, and a loan left blank read "0.00%" of both as though
  // that had been measured.
  if (budget > 0) {
    tiles.push(
      [
        'budgetOfLoanAmount',
        'Budget % of Loan Amount',
        getPrice2decimal((budget / input.proposedLoanAmount) * 100),
        '%',
      ],
      [
        'budgetOfAfterRepairValue',
        'Budget % of After Repair Value',
        getPrice2decimal((budget / input.afterRepairValue) * 100),
        '%',
      ],
    )
  }

  return tiles
}
